import { useUploadFile } from '@/api/file/file.hooks';
import {
  useGetCategories,
  useUpdateExercise,
} from '@/api/glow-up/glow-up.hooks';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Exercise, UpdateExerciseRequest } from '@repo/types/src/glow-up';
import { Edit, FileAudio, Upload, X } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

type EditExerciseProps = {
  exercise: Exercise;
  refetch: () => void;
};

export const EditExercise = ({ exercise, refetch }: EditExerciseProps) => {
  const mutation = useUpdateExercise();
  const uploadFileMutation = useUploadFile();
  const { categories } = useGetCategories();
  const [open, setOpen] = useState(false);
  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [selectedAudioFile, setSelectedAudioFile] = useState<File | null>(null);
  const [formData, setFormData] = useState({
    categoryId: exercise.categoryId.toString(),
    title: exercise.title,
    description: exercise.description,
    duration: exercise.duration.toString(),
    image: exercise.image,
    audio: exercise.audio,
  });

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedImageFile(file);
      setFormData({ ...formData, image: file.name });
    }
  };

  const handleAudioSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedAudioFile(file);
      setFormData({ ...formData, audio: file.name });
    }
  };

  const handleRemoveImage = () => {
    setSelectedImageFile(null);
    setFormData({ ...formData, image: exercise.image }); // Reset to original image
  };

  const handleRemoveAudio = () => {
    setSelectedAudioFile(null);
    setFormData({ ...formData, audio: exercise.audio }); // Reset to original audio
  };

  const handleUpdate = async () => {
    if (
      !formData.categoryId ||
      !formData.title ||
      !formData.description ||
      !formData.duration
    ) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      let imageUrl = exercise.image; // Use existing image by default
      let audioUrl = exercise.audio; // Use existing audio by default

      // Upload new files if they were selected
      const uploadPromises = [];

      if (selectedImageFile) {
        uploadPromises.push(
          uploadFileMutation.mutateAsync(selectedImageFile).then((result) => {
            imageUrl = result.url;
          }),
        );
      }

      if (selectedAudioFile) {
        uploadPromises.push(
          uploadFileMutation.mutateAsync(selectedAudioFile).then((result) => {
            audioUrl = result.url;
          }),
        );
      }

      if (uploadPromises.length > 0) {
        toast.info('Upload des fichiers en cours...');
        await Promise.all(uploadPromises);
      }

      const payload: UpdateExerciseRequest = {
        categoryId: parseInt(formData.categoryId, 10),
        title: formData.title,
        description: formData.description,
        duration: parseInt(formData.duration, 10),
        image: imageUrl,
        audio: audioUrl,
      };

      mutation.mutate(
        { id: exercise.id, payload },
        {
          onSuccess: (data) => {
            setOpen(false);
            setSelectedImageFile(null);
            setSelectedAudioFile(null);
            refetch();
            toast.success(
              `L'exercice ${data.title} a été modifié avec succès!`,
              {
                duration: 3000,
              },
            );
          },
          onError: () => {
            toast.error(`Erreur lors de la modification de l'exercice`, {
              duration: 3000,
            });
          },
        },
      );
    } catch (error) {
      toast.error("Erreur lors de l'upload des fichiers");
    }
  };

  const isLoading = uploadFileMutation.isPending || mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit size={16} />
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-center">Modifier l'exercice</DialogTitle>
          <DialogDescription className="text-center">
            Modifier les informations de l'exercice
          </DialogDescription>
        </DialogHeader>

        <div className="w-full flex flex-col space-y-4">
          <div>
            <Select
              value={formData.categoryId}
              onValueChange={(value) =>
                setFormData({ ...formData, categoryId: value })
              }
              disabled={isLoading}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner une catégorie" />
              </SelectTrigger>
              <SelectContent>
                {categories?.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {category.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Input
              type="text"
              placeholder="Titre de l'exercice"
              value={formData.title}
              onChange={(e) =>
                setFormData({ ...formData, title: e.target.value })
              }
              disabled={isLoading}
            />
          </div>

          <div>
            <Input
              type="text"
              placeholder="Description de l'exercice"
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              disabled={isLoading}
            />
          </div>

          <div>
            <Input
              type="number"
              placeholder="Durée en secondes"
              value={formData.duration}
              onChange={(e) =>
                setFormData({ ...formData, duration: e.target.value })
              }
              disabled={isLoading}
            />
          </div>

          {/* Image Upload Section */}
          <div>
            <label className="text-sm font-medium mb-2 block">Image</label>
            {!selectedImageFile ? (
              <div>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <Upload className="mx-auto h-8 w-8 text-gray-400" />
                  <div className="mt-2">
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <span className="text-blue-600 hover:text-blue-500">
                        Cliquez pour changer l'image
                      </span>
                    </label>
                    <input
                      id="image-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handleImageSelect}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {exercise.image && (
                  <p className="text-xs text-gray-500 mt-2">
                    Image actuelle: {exercise.image}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="bg-blue-100 p-2 rounded">
                    <Upload className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedImageFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedImageFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveImage}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>

          {/* Audio Upload Section */}
          <div>
            <label className="text-sm font-medium mb-2 block">Audio</label>
            {!selectedAudioFile ? (
              <div>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  <FileAudio className="mx-auto h-8 w-8 text-gray-400" />
                  <div className="mt-2">
                    <label htmlFor="audio-upload" className="cursor-pointer">
                      <span className="text-purple-600 hover:text-purple-500">
                        Cliquez pour changer la audio
                      </span>
                    </label>
                    <input
                      id="audio-upload"
                      type="file"
                      accept="audio/*"
                      className="hidden"
                      onChange={handleAudioSelect}
                      disabled={isLoading}
                    />
                  </div>
                </div>
                {exercise.audio && (
                  <p className="text-xs text-gray-500 mt-2">
                    Audio actuelle: {exercise.audio}
                  </p>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className="bg-purple-100 p-2 rounded">
                    <FileAudio className="h-4 w-4 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {selectedAudioFile.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {(selectedAudioFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleRemoveAudio}
                  disabled={isLoading}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose asChild>
            <Button variant="ghost" disabled={isLoading}>
              Annuler
            </Button>
          </DialogClose>
          <Button variant="default" onClick={handleUpdate} disabled={isLoading}>
            {isLoading ? 'Modification...' : 'Modifier'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
